import { useEffect, useRef, type ReactNode } from 'react'
import { Breadcrumbs, Button, Menu, breakpointQueries, useMediaQuery } from '@systemeio/components'
import { ChevronDownIcon, ChevronLeftIcon, Save01Icon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { UserRolesEnum } from 'shared/enums/user-roles-enum'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import ProfileMenuSkeleton from './profile-menu-skeleton'

export function ProfileLayout({
  isFetching,
  onSave,
  disabled,
  title,
  children,
}: {
  isFetching?: boolean
  onSave?: () => void
  disabled?: boolean
  title: string
  children: ReactNode
}): ReactNode {
  const { t } = useTranslation()
  const router = useRouter()
  const { user, loading, showChangePasswordPage } = useUser()
  const navigationRef = useRef<HTMLElement>(null)
  const isDesktop = useMediaQuery(breakpointQueries.isDesktop, {
    initializeWithValue: false,
  })
  const authorized = isAuthorizedUser(user)

  useEffect(() => {
    const navigation = navigationRef.current
    if (!authorized || isDesktop || !navigation?.clientWidth) return
    const active = navigation.querySelector<HTMLElement>('[aria-current="page"]')
    if (!active) return
    const viewport = navigation.getBoundingClientRect()
    const link = active.getBoundingClientRect()
    if (link.left < viewport.left) {
      navigation.scrollBy({
        left: Math.floor(link.left - viewport.left),
        behavior: 'instant',
      })
    } else if (link.right > viewport.right) {
      navigation.scrollBy({
        left: Math.ceil(link.right - viewport.right),
        behavior: 'instant',
      })
    }
  }, [authorized, isDesktop, router.locale, router.pathname])

  if (!authorized) return <div>{children}</div>

  const hasCustomerRole = user.roles.includes(UserRolesEnum.Customer)
  const navigation = [
    {
      href: '/profile/user-settings',
      label: t('profile.navigation.profile'),
      mobileLabel: t('profile.navigation.profile'),
      show: !user.workspaceOwner,
    },
    {
      href: '/profile/account-settings',
      label: t('profile.navigation.account_settings'),
      mobileLabel: t('profile.navigation.account_settings'),
      show: !hasCustomerRole && !user.impersonator,
    },
    {
      href: '/profile/manage-subscriptions',
      label: t('profile.navigation.subscriptions'),
      mobileLabel: t('profile.navigation.subscriptions'),
      show: !user.workspaceOwner,
    },
    {
      href:
        hasCustomerRole && !isCustomDomain
          ? getDashboardNotificationSettingsLink(user)
          : '/profile/notification-settings',
      label: t('profile.user_settings.manage_notifications.title'),
      mobileLabel: t('profile.user_settings.notifications.title'),
      show: true,
    },
    {
      href: '/profile/change-password',
      label: t('profile.user_settings.change_password.title'),
      mobileLabel: t('profile.navigation.security'),
      show: showChangePasswordPage,
    },
  ].filter(item => item.show)
  const currentLabel = navigation.find(item => item.href === router.pathname)?.label ?? title
  const breadcrumbsItems = [
    { label: t('user_settings.title') },
    { label: currentLabel, current: true },
  ]

  return (
    <div className="flex flex-1 flex-col">
      <h1 className="sr-only">{title}</h1>
      <div className="mx-auto flex min-h-16 w-full max-w-(--breakpoint-2xl) items-center justify-between gap-4 px-4 tablet:min-h-14 tablet:px-6 lg:min-h-16 lg:px-14">
        <Breadcrumbs className="hidden lg:block" items={breadcrumbsItems} />
        <div className="flex min-w-0 items-center gap-2 lg:hidden">
          <Link
            href="/school"
            aria-label={t('header.back_to_home_page')}
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-text-secondary outline-none focus-visible:focus-ring-primary"
          >
            <ChevronLeftIcon className="size-4" />
          </Link>
          <Menu
            align="start"
            drawerTitle={t('user_settings.title')}
            trigger={
              <button
                type="button"
                className="inline-flex items-center gap-1 text-base text-text-link"
              >
                {t('user_settings.title')}
                <ChevronDownIcon className="size-4" />
              </button>
            }
            triggerProps={{ 'aria-label': t('user_settings.title') }}
            items={navigation.map(item => ({
              id: item.href,
              type: 'link',
              label: item.label,
              render: <Link href={item.href} />,
              state: item.href === router.pathname ? 'active' : undefined,
            }))}
          />
        </div>
        {onSave && (
          <Button
            variant="primary"
            size="sm"
            className="hidden tablet:inline-flex"
            data-testid="save-button"
            aria-label={t('global.save')}
            isLoading={isFetching}
            disabled={disabled}
            onClick={onSave}
          >
            <Save01Icon className="size-4" />
            {t('global.save')}
          </Button>
        )}
      </div>
      <nav
        ref={navigationRef}
        aria-label={t('user_settings.title')}
        className="scrollbar-none overflow-x-auto overscroll-x-contain lg:hidden [&::-webkit-scrollbar]:hidden"
      >
        <ul className="flex min-w-max border-b border-border-200 px-4 tablet:px-6">
          {navigation.map(item => (
            <li key={item.href}>
              <Link
                href={item.href}
                aria-current={item.href === router.pathname ? 'page' : undefined}
                className={cn(
                  '-mb-px block border-b-2 border-transparent px-3 pb-4 text-base/5 font-medium whitespace-nowrap text-text-secondary outline-none focus-visible:focus-ring-primary',
                  item.href === router.pathname && 'border-border-accent text-text-link',
                )}
              >
                {item.mobileLabel}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="mx-auto grid w-full max-w-(--breakpoint-2xl) flex-1 content-start gap-6 px-4 pt-5 pb-6 [grid-template-areas:'main'] tablet:px-6 lg:grid-cols-[15rem_minmax(0,1fr)] lg:px-14 lg:pt-0 lg:[grid-template-areas:'sidebar_main']">
        <nav aria-label={t('user_settings.title')} className="hidden h-fit lg:block">
          {loading ? (
            <ProfileMenuSkeleton />
          ) : (
            <ul className="flex flex-col gap-1 rounded-lg border border-border-card bg-bg-primary px-2 py-4">
              {navigation.map(item => {
                const isActive = item.href === router.pathname
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        'flex min-h-11 items-center rounded-lg px-4 py-3 text-base/5 text-text-secondary outline-none focus-visible:focus-ring-primary',
                        isActive ? 'bg-bg-accent-light text-text-link' : 'hover:bg-bg-secondary',
                      )}
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </nav>
        <main className="min-w-0">{children}</main>
      </div>
      {onSave && (
        <footer className="mt-auto bg-bg-primary p-4 tablet:hidden">
          <Button
            variant="primary"
            size="md"
            className="w-full"
            data-testid="save-button-mobile"
            aria-label={t('global.save')}
            isLoading={isFetching}
            disabled={disabled}
            onClick={onSave}
          >
            <Save01Icon className="size-5" />
            {t('global.save')}
          </Button>
        </footer>
      )}
    </div>
  )
}
